import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataRoomAccessPermission } from 'shared';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon, SearchIcon, XCloseIcon } from '../../../../../assets/icons/index';
import MinusIcon from '../../../../../assets/icons/minus-icon';
import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import { Checkbox } from '../../../../../components/checkbox/index';
import Loader from '../../../../../components/loader/index';
import {
  Select,
  SelectButton,
  SelectOption,
  SelectOptions,
} from '../../../../../components/select/index';
import { Tag, TagVariant } from '../../../../../components/tag/index';
import { useLocale } from '../../../../../hooks/index';
import {
  ShareCandidate,
  useShareCandidateOrganisations,
  useShareCandidates,
} from '../../../../../hooks/space/index';
import { useDebounce } from '../../../../../hooks/use-debounce.hook';
import { StringKey } from '../../../../../lang/index';
import { PageRoute } from '../../../../../types/page.types';
import { StakeholderType, stakeholderTypeTitle } from '../../../../../types/stakeholder.types';
import { DataRoomModalStore } from '../../../store/data-room-modal.store';
import { AccessAvatar } from './access-avatar';
import { InternalEmptyState } from './internal-empty-state';
import { SelectedInternal } from './invite-people-modal';
import { InvitePermissionToggle } from './invite-permission-toggle';

const PAGE_SIZE = 100;

/**
 * The four columns the design lays the list out in: 269 for the person, 97 for their type, 94 for
 * their standing and 230 for the permission, inside the 691 the blade leaves for content. Fixed,
 * but able to give ground — a scrollbar beside a long list must not push the row sideways.
 */
const COLUMN = {
  // Only the person gives ground. The other three hold what they are drawn at — squeezed, the
  // permission group wraps onto a second line and the row stops being 72 tall.
  permission: 'w-[230px] shrink-0',
  person: 'w-[269px] min-w-0 flex-1',
  status: 'w-[94px] shrink-0',
  // The 97 the design draws is a floor, not a ceiling: a type is a fixed word from a short list,
  // and half of them are longer than that. Cutting "Employee" to "Employ…" saves nothing the
  // person column cannot give up instead.
  type: 'min-w-[97px] shrink-0 whitespace-nowrap',
};

const TYPE_OPTIONS: { label: StringKey; value: '' | StakeholderType }[] = [
  { label: 'ALL_TYPE', value: '' },
  ...Object.values(StakeholderType).map((type) => ({
    label: stakeholderTypeTitle[type],
    value: type,
  })),
];

const FILTER_BUTTON =
  'flex h-9 items-center justify-between gap-2 rounded border border-gray-100 bg-white py-1.5 pl-3 pr-1.5 shadow-xs';
const FILTER_LIST =
  'pointer-events-auto z-[60] mt-0 max-h-[216px] min-w-40 overflow-y-auto rounded border border-gray-100 bg-white shadow-lg';
const FILTER_OPTION = 'block px-3 py-2 text-sm text-gray-700 hover:bg-gray-50';

const FilterChevron: FC<{ isOpen: boolean }> = ({ isOpen }) => (
  <span className="flex size-5 shrink-0 items-center justify-center">
    <ChevronDownIcon
      className={twMerge('size-[13px] shrink-0 transition-transform', isOpen && 'rotate-180')}
    />
  </span>
);

export type InviteInternalTabProps = {
  dataRoomId: string;
  onPermissionChange: (id: string, permission: DataRoomAccessPermission) => void;
  onToggle: (candidate: ShareCandidate, isSelected: boolean) => void;
  selected: SelectedInternal[];
};

export const InviteInternalTab: FC<InviteInternalTabProps> = ({
  dataRoomId,
  onPermissionChange,
  onToggle,
  selected,
}) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<'' | StakeholderType>('');
  const [organisation, setOrganisation] = useState('');
  // The permission chosen on a row that is not selected yet, kept so ticking it later honours
  // the choice instead of starting again at Viewer.
  const [draftPermissions, setDraftPermissions] = useState<
    Record<string, DataRoomAccessPermission>
  >({});

  const { debounceCallback } = useDebounce<string>({ callback: setDebouncedSearch });

  const { organisations } = useShareCandidateOrganisations(dataRoomId);
  const { isError, isLoading, records } = useShareCandidates(dataRoomId, {
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...(typeFilter ? { type: typeFilter } : {}),
    ...(organisation ? { organisation } : {}),
    take: PAGE_SIZE,
  });

  const { messagesLocale } = useLocale();
  const navigate = useNavigate();
  const handleDismiss = DataRoomModalStore(({ handleDismiss }) => handleDismiss);

  const selectedIds = new Set(selected.map(({ candidate }) => candidate.id));
  const selectable = records.filter((candidate) => !candidate.hasAccess);
  const selectedSelectable = selectable.filter((candidate) => selectedIds.has(candidate.id));
  const isPartlySelected =
    selectedSelectable.length > 0 && selectedSelectable.length < selectable.length;

  const toggleAll = () => {
    const isSelecting = selectedSelectable.length === 0;

    selectable
      .filter((candidate) => selectedIds.has(candidate.id) !== isSelecting)
      .forEach((candidate) => toggleCandidate(candidate, isSelecting));
  };

  /**
   * What each row's control shows. A selected row reads from the selection, which is the one
   * that will be sent; an unselected row reads from the choice made on it before it was ticked.
   *
   * The permission can be set first and the person ticked after, in either order: a control that
   * only works once the box beside it is ticked reads as broken, since nothing on the row says
   * that is the rule.
   */
  const permissionOf = (id: string) =>
    selected.find(({ candidate }) => candidate.id === id)?.permission ??
    draftPermissions[id] ??
    DataRoomAccessPermission.VIEWER;

  const changePermission = (candidate: ShareCandidate, permission: DataRoomAccessPermission) => {
    setDraftPermissions((current) => ({ ...current, [candidate.id]: permission }));

    if (selectedIds.has(candidate.id)) onPermissionChange(candidate.id, permission);
  };

  // Ticking a row carries whatever permission was already chosen on it; the parent adds every
  // new selection as a Viewer, so the choice is applied straight after.
  const toggleCandidate = (candidate: ShareCandidate, isSelected: boolean) => {
    onToggle(candidate, isSelected);

    const draft = draftPermissions[candidate.id];

    if (isSelected && draft) onPermissionChange(candidate.id, draft);
  };

  const hasFilters = Boolean(debouncedSearch || typeFilter || organisation);

  if (!isLoading && !isError && records.length === 0 && !hasFilters) {
    return (
      <InternalEmptyState
        onAddStakeholder={() => {
          handleDismiss();
          navigate(PageRoute.STAKEHOLDERS);
        }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex h-9 items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Select<'' | StakeholderType> onChange={setTypeFilter} value={typeFilter}>
            {({ open }: { open: boolean }) => (
              <div className="relative">
                <SelectButton className={FILTER_BUTTON}>
                  <span className="text-sm font-[450] text-gray-700">
                    <AppFormattedMessage
                      id={
                        TYPE_OPTIONS.find((option) => option.value === typeFilter)?.label ??
                        'ALL_TYPE'
                      }
                    />
                  </span>
                  <FilterChevron isOpen={open} />
                </SelectButton>
                <SelectOptions
                  anchor={{ gap: 4, to: 'bottom start' }}
                  className={FILTER_LIST}
                  portal
                >
                  {TYPE_OPTIONS.map((option) => (
                    <SelectOption key={option.value || 'all'} value={option.value}>
                      <span className={FILTER_OPTION}>
                        <AppFormattedMessage id={option.label} />
                      </span>
                    </SelectOption>
                  ))}
                </SelectOptions>
              </div>
            )}
          </Select>

          <Select<string> onChange={setOrganisation} value={organisation}>
            {({ open }: { open: boolean }) => (
              <div className="relative">
                <SelectButton className={FILTER_BUTTON}>
                  <span className="text-sm font-[450] text-gray-700">
                    {organisation || <AppFormattedMessage id="ALL_ORG" />}
                  </span>
                  <FilterChevron isOpen={open} />
                </SelectButton>
                <SelectOptions
                  anchor={{ gap: 4, to: 'bottom start' }}
                  className={FILTER_LIST}
                  portal
                >
                  <SelectOption value="">
                    <span className={FILTER_OPTION}>
                      <AppFormattedMessage id="ALL_ORG" />
                    </span>
                  </SelectOption>
                  {organisations.map((name) => (
                    <SelectOption key={name} value={name}>
                      <span className={FILTER_OPTION}>{name}</span>
                    </SelectOption>
                  ))}
                </SelectOptions>
              </div>
            )}
          </Select>
        </div>

        {/* 272px of field with the magnifier at its right edge, underlined rather than boxed. */}
        <label className="flex h-9 w-[272px] shrink-0 items-center gap-2 rounded-t border-b border-gray-700 bg-gray-50 p-2">
          <input
            className="w-full bg-transparent text-sm font-[450] leading-5 text-gray-700 outline-none placeholder:text-gray-400"
            onChange={(event) => {
              setSearch(event.target.value);
              debounceCallback(event.target.value);
            }}
            placeholder={messagesLocale['SEARCH']}
            value={search}
          />
          {/* The way back to the whole list, as the toolbar search beside it has: without it a
              typed query can only be cleared a character at a time. */}
          {search && (
            <button
              aria-label={messagesLocale['CLEAR_SEARCH']}
              className="flex size-4 shrink-0 items-center justify-center"
              onClick={() => {
                setSearch('');
                setDebouncedSearch('');
              }}
              type="button"
            >
              <XCloseIcon className="size-4" iconColor="#344054" />
            </button>
          )}
          <SearchIcon className="size-5 shrink-0" />
        </label>
      </div>

      <div className="w-full overflow-hidden rounded-md border border-gray-100">
        <div className="flex h-11 items-center gap-3 bg-gray-50 px-2">
          <Checkbox
            checked={selectedSelectable.length > 0}
            disabled={!selectable.length}
            onChange={toggleAll}
            selectIcon={isPartlySelected ? <MinusIcon /> : undefined}
          />
          <span className="text-label-md font-medium text-[#172335]">
            <AppFormattedMessage
              id="STAKEHOLDERS_SELECTED_HEADER"
              values={{ count: selected.length }}
            />
          </span>
        </div>

        {isLoading && (
          <div className="p-6">
            <Loader />
          </div>
        )}

        {!isLoading && isError && (
          <p className="p-6 text-sm font-[450] text-fireside-500">
            <AppFormattedMessage id="SHARE_CANDIDATES_LOAD_FAILED" />
          </p>
        )}

        {!isLoading && !isError && records.length === 0 && (
          <p className="p-6 text-sm font-[450] text-gray-500">
            <AppFormattedMessage id="NO_STAKEHOLDERS_TO_INVITE" />
          </p>
        )}

        {!isLoading &&
          !isError &&
          records.map((candidate) => (
            <div className="flex h-[72px] items-center border-t border-gray-100" key={candidate.id}>
              <div className={twMerge('flex items-center gap-3 pl-2', COLUMN.person)}>
                {/* Already on the list: the row stays visible so an Admin can see they are
                    covered, but it cannot be selected again — and it is drawn as the dead
                    control it is, rather than looking like a box nobody has ticked yet. */}
                <Checkbox
                  checked={selectedIds.has(candidate.id)}
                  className={candidate.hasAccess ? 'bg-gray-50 ring-gray-200' : undefined}
                  disabled={candidate.hasAccess}
                  onChange={(isSelected: boolean) => toggleCandidate(candidate, isSelected)}
                />
                <AccessAvatar
                  label={candidate.name}
                  labelClassName="text-[12.8px] leading-[19.2px]"
                />
                <div className="flex min-w-0 flex-1 flex-col">
                  <p className="truncate text-sm font-[450] leading-5 text-gray-700">
                    {candidate.name}
                  </p>
                  <p className="truncate text-label-sm font-medium leading-4 text-gray-400">
                    {candidate.email}
                  </p>
                </div>
              </div>

              <span className={twMerge('px-4 text-sm font-[450] text-gray-700', COLUMN.type)}>
                <AppFormattedMessage id={stakeholderTypeTitle[candidate.type]} />
              </span>

              {/* The person's standing in the COMPANY — not their status in this room. */}
              <div className={twMerge('px-4', COLUMN.status)}>
                <Tag
                  title={<AppFormattedMessage id={candidate.isActive ? 'ACTIVE' : 'INACTIVE'} />}
                  variant={candidate.isActive ? TagVariant.GREEN : TagVariant.YELLOW}
                />
              </div>

              <div className={twMerge('flex items-center px-2', COLUMN.permission)}>
                {candidate.hasAccess ? (
                  <span className="text-label-md text-gray-400">
                    <AppFormattedMessage id="ALREADY_HAS_ACCESS" />
                  </span>
                ) : (
                  <InvitePermissionToggle
                    onChange={(permission) => changePermission(candidate, permission)}
                    value={permissionOf(candidate.id)}
                  />
                )}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};
